import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, Clock, ArrowRight } from 'lucide-react';
import { servicesAPI } from '../api';
import { useToast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { getFaqsForService, buildFaqSchema } from '../data/serviceFaqs';
import { getComparisonForService } from '../data/serviceComparisons';

const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://consultiumgroup.co.za').replace(/\/$/, '');

const ServiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [service, setService] = useState(null);
  const [relatedServices, setRelatedServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchService = async () => {
      try {
        const response = await servicesAPI.getById(id);
        setService(response.data);
        
        // Fetch related services
        const allServicesRes = await servicesAPI.getAll({ category: response.data.category });
        const related = allServicesRes.data.filter(s => s.id !== id).slice(0, 3);
        setRelatedServices(related);
      } catch (error) {
        console.error('Error fetching service:', error);
        toast({
          title: 'Error',
          description: 'Service not found',
          variant: 'destructive'
        });
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    
    fetchService();
  }, [id, navigate, toast]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h1 className="text-3xl font-bold text-gray-800 mb-4">Service Not Found</h1>
          <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
            Back to Home
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const handleAddToCart = () => {
    toast({
      title: 'Added to Cart',
      description: `${service.name} has been added to your cart.`,
    });
  };

  const handleCheckout = () => {
    navigate(`/service/${service.id}/form`);
  };

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Consultium Group',
      url: SITE_URL,
      telephone: '+27128243708',
      email: 'hello@consultiumgroup.co.za',
    },
    areaServed: { '@type': 'Country', name: 'South Africa' },
    serviceType: service.category || 'Business Services',
    ...(service.enquiryOnly
      ? {}
      : {
          offers: {
            '@type': 'Offer',
            price: String(service.price),
            priceCurrency: 'ZAR',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/service/${service.id}`,
          },
        }),
  };

  // Standard 4-step "how we deliver" HowTo schema. Provides Google + AI
  // engines with an extractable process breakdown for every service page.
  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to order ${service.name} through Consultium Group`,
    description: `Order ${service.name} online with Consultium Group — enquiry, secure payment, document review, and CIPC/SARS submission.`,
    ...(service.enquiryOnly
      ? {}
      : { estimatedCost: { '@type': 'MonetaryAmount', currency: 'ZAR', value: String(service.price) } }),
    step: [
      {
        '@type': 'HowToStep',
        position: 1,
        name: 'Select the service',
        text: `Open the ${service.name} page and click Order or Get Quote to start the request.`,
      },
      {
        '@type': 'HowToStep',
        position: 2,
        name: 'Complete the online form',
        text: 'Provide the required company or personal details, upload any supporting documents (certified ID, proof of address, existing CIPC/SARS records) and confirm your instructions.',
      },
      {
        '@type': 'HowToStep',
        position: 3,
        name: 'Secure payment',
        text: 'Pay by manual EFT (bank transfer) or the online payment options provided at checkout, and upload proof of payment.',
      },
      {
        '@type': 'HowToStep',
        position: 4,
        name: 'Delivery of completed work',
        text: `Consultium files with CIPC or SARS on your behalf and delivers the final documents ${service.duration ? `within ${service.duration}` : 'once regulator processing is complete'}.`,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title={`${service.name} | Consultium Group`}
        description={`${service.description} ${service.enquiryOnly ? 'Get a personalised quote.' : `From R${service.price} — turnaround ${service.duration}.`}`}
        path={`/service/${service.id}`}
      />
      <StructuredData data={serviceSchema} />
      <StructuredData data={howToSchema} />
      {(() => {
        const faqs = getFaqsForService(service.id);
        return faqs.length > 0 ? (
          <StructuredData data={buildFaqSchema(faqs)} />
        ) : null;
      })()}
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/' },
            { label: 'Services', to: '/#popular' },
            { label: service.name },
          ]}
        />
        <div className="grid md:grid-cols-2 gap-12">
          <div>
            <img 
              src={service.image}
              alt={service.name}
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="w-full h-96 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div>
            <div className="mb-6">
              <h1 className="text-4xl font-bold text-gray-800 mb-2">{service.name}</h1>
              <p className="text-gray-600">{service.description}</p>
            </div>

            <div className="mb-8">
              {service.enquiryOnly ? (
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-3xl font-bold text-emerald-600">Price on Enquiry</span>
                </div>
              ) : (
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-5xl font-bold text-emerald-600">R{service.price}</span>
                  {service.additionalFee && (
                    <span className="text-gray-600">+ {service.additionalFee}</span>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="h-5 w-5" />
                <span>{service.enquiryOnly ? 'Turnaround:' : 'Processing time:'} {service.duration}</span>
              </div>
            </div>

            <Card className="mb-8">
              <CardHeader>
                <CardTitle>What&apos;s Included</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {service.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button 
                onClick={handleCheckout}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-lg py-6"
                data-testid="start-application-btn"
              >
                {service.enquiryOnly ? 'Request a Quote' : 'Start Application'} <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              {!service.enquiryOnly && (
                <Button 
                  onClick={handleAddToCart}
                  variant="outline"
                  className="px-8 py-6 text-lg"
                >
                  Add to Cart
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {relatedServices.map(relatedService => (
              <Card key={relatedService.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/service/${relatedService.id}`)}>
                <CardHeader className="p-0">
                  <img 
                    src={relatedService.image}
                    alt={relatedService.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                </CardHeader>
                <CardContent className="pt-4">
                  <h3 className="font-bold text-lg mb-2">{relatedService.name}</h3>
                  <p className="text-2xl font-bold text-emerald-600">R{relatedService.price}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {(() => {
          const cmp = getComparisonForService(service.id);
          if (!cmp) return null;
          return (
            <section
              className="mt-16"
              aria-labelledby="service-comparison-heading"
              data-testid="service-comparison"
            >
              <h2
                id="service-comparison-heading"
                className="text-2xl font-bold text-gray-800 mb-2"
              >
                {cmp.title}
              </h2>
              <p className="text-gray-600 max-w-3xl mb-5">{cmp.intro}</p>
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="min-w-full text-sm text-left">
                  <thead className="bg-emerald-50 text-emerald-900">
                    <tr>
                      {cmp.headers.map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 font-semibold border-b border-emerald-100 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cmp.rows.map((row, rIdx) => (
                      <tr
                        key={`cmp-row-${rIdx}`}
                        className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                      >
                        {row.map((cell, cIdx) => (
                          <td
                            key={`cmp-cell-${rIdx}-${cIdx}`}
                            className={`px-4 py-3 align-top text-gray-700 border-b border-gray-100 ${cIdx === 0 ? 'font-semibold text-gray-900' : ''}`}
                          >
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {cmp.footnote && (
                <p className="mt-3 text-xs text-gray-500 max-w-3xl">{cmp.footnote}</p>
              )}
            </section>
          );
        })()}

        {(() => {
          const faqs = getFaqsForService(service.id);
          if (faqs.length === 0) return null;
          return (
            <section
              className="mt-16"
              aria-labelledby="service-faqs-heading"
              data-testid="service-faqs"
            >
              <h2
                id="service-faqs-heading"
                className="text-2xl font-bold text-gray-800 mb-6"
              >
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((item, idx) => (
                  <AccordionItem
                    key={`${service.id}-faq-${idx}`}
                    value={`${service.id}-faq-${idx}`}
                    className="border rounded-lg px-5 bg-white"
                  >
                    <AccordionTrigger
                      className="text-left font-semibold hover:text-emerald-600"
                      data-testid={`service-faq-q-${idx}`}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 leading-relaxed">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <p className="mt-4 text-sm text-gray-500">
                Have more questions?{' '}
                <a href="/faqs" className="text-emerald-700 hover:underline font-medium">
                  Browse the full FAQ knowledge base →
                </a>
              </p>
            </section>
          );
        })()}
      </div>

      <Footer />
    </div>
  );
};

export default ServiceDetail;
